import React from 'react';
import { Loader2, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export interface Column<T> {
  id: keyof T | string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: unknown, row: T) => React.ReactNode;
   /** Sıralanabilir kolonlar için */
  sortable?: boolean;
  /** Backend veya client-side sort anahtarı – boşsa id kullanılır */
  sortKey?: keyof T | string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  onRowDoubleClick?: (row: T) => void;
  onRowClick?: (row: T) => void;
  getRowId?: (row: T, index?: number) => string | number;
  selectedRowId?: string | number;
  /** Aktif sıralama bilgisi (opsiyonel) */
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (sortBy: string, sortDir: 'asc' | 'desc') => void;
}

export function DataTable<T extends Record<string, any>>({
  columns,
  rows,
  loading = false,
  onRowDoubleClick,
  onRowClick,
  getRowId,
  selectedRowId,
  sortBy,
  sortDir = 'asc',
  onSortChange,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <div className="card p-12 flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-accent animate-spin mb-4" />
        <p className="text-sm text-foreground-secondary">Yükleniyor...</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="card p-12 flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-16 h-16 rounded-full bg-background-tertiary flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-foreground-tertiary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
        </div>
        <p className="text-sm font-medium text-foreground mb-1">Veri bulunamadı</p>
        <p className="text-xs text-foreground-tertiary">Henüz kayıt eklenmemiş</p>
      </div>
    );
  }

  const hasClickHandler = onRowDoubleClick || onRowClick;

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto custom-scrollbar px-0 md:px-0">
        <table className="table min-w-full">
          <thead className="table-header">
            <tr>
              {columns.map((column, colIndex) => (
                <th
                  key={String(column.id)}
                  className={`table-header-cell text-xs md:text-sm ${
                    colIndex === 0 ? 'pl-4 md:pl-4' : ''
                  } ${
                    column.align === 'right'
                      ? 'text-right'
                      : column.align === 'center'
                      ? 'text-center'
                      : 'text-left'
                  } ${column.sortable && onSortChange ? 'cursor-pointer select-none' : ''}`}
                  style={{ minWidth: column.minWidth }}
                  onClick={() => {
                    if (!column.sortable || !onSortChange) return;
                    const key = String(column.sortKey ?? column.id);
                    const isActive = sortBy === key;
                    const nextDir: 'asc' | 'desc' =
                      isActive && sortDir === 'asc' ? 'desc' : 'asc';
                    onSortChange(key, nextDir);
                  }}
                >
                  <span className="inline-flex items-center gap-1">
                    <span>{column.label}</span>
                    {column.sortable && onSortChange && (
                      <span className="text-[10px] text-foreground-tertiary">
                        {(() => {
                          const key = String(column.sortKey ?? column.id);
                          const isActive = sortBy === key;
                          if (!isActive) {
                            return <ChevronsUpDown className="w-3 h-3" />;
                          }
                          return sortDir === 'asc' ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          );
                        })()}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const rowId = getRowId ? getRowId(row, index) : index;
              const isSelected = selectedRowId !== undefined && rowId === selectedRowId;
              return (
                <tr
                  key={rowId}
                  onClick={() => onRowClick?.(row)}
                  onDoubleClick={() => onRowDoubleClick?.(row)}
                  className={`table-row transition-colors ${
                    isSelected
                      ? 'bg-accent/20 border-l-4 border-l-accent hover:bg-accent/25'
                      : hasClickHandler
                      ? 'cursor-pointer hover:bg-background-secondary active:bg-background-tertiary'
                      : ''
                  }`}
                >
                  {columns.map((column, colIndex) => {
                    const value = column.id in row ? row[column.id as keyof T] : '';
                    const cellContent = column.format
                      ? column.format(value, row)
                      : String(value ?? '');
                    
                    return (
                      <td
                        key={String(column.id)}
                        className={`table-cell text-xs md:text-sm py-2 md:py-3 ${
                          colIndex === 0 
                            ? 'pl-4 pr-2 md:pl-4 md:pr-4' 
                            : 'px-2 md:px-4'
                        } ${
                          column.align === 'right'
                            ? 'text-right'
                            : column.align === 'center'
                            ? 'text-center'
                            : 'text-left'
                        }`}
                      >
                        {cellContent}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
